import { useTranslation } from "react-i18next";
import type {
  BodyPartKey,
  TrackerKey,
} from "../../../../../types/clientConfig";
import { ConfigSwitch } from "../../../../athlete-portal/profile/components/ClientConfiguration/ConfigSwitch";
import {
  BODY_PART_I18N_KEYS,
  isSyncableTracker,
} from "../../../../athlete-portal/profile/components/ClientConfiguration/utils";
import { SectionLabel } from "./SectionLabel";
import {
  countActiveMetrics,
  PUSH_METRIC_ORDER,
  type PushDashboardFormState,
  TRAINING_DAY_METRICS,
  toggleBodyPart,
} from "./utils";

export const TrackingCard = ({
  state,
  allBodyParts,
  onMetricChange,
  onBodyPartsChange,
}: {
  state: PushDashboardFormState;
  allBodyParts: BodyPartKey[];
  onMetricChange: (key: TrackerKey, value: boolean) => void;
  onBodyPartsChange: (parts: BodyPartKey[]) => void;
}) => {
  const { t } = useTranslation("dashboard");
  const allSelected = state.bodyParts.length === allBodyParts.length;
  const onlyOneLeft = state.bodyParts.length === 1;
  const atLeastOne = t("pushDailyDashboard.bodyParts.atLeastOne");
  const bodyPartLabel = (part: BodyPartKey) =>
    BODY_PART_I18N_KEYS[part]
      ? t(
          `athlete-portal:clientConfiguration.bodyParts.${BODY_PART_I18N_KEYS[part]}`
        )
      : part;

  return (
    <>
      <SectionLabel
        label={t("pushDailyDashboard.tracking")}
        pill={
          <span className="push-dd-pill push-dd-pill--info">
            {t("pushDailyDashboard.activeCount", {
              count: countActiveMetrics(state),
            })}
          </span>
        }
      />
      <div className="push-dd-grid">
        {PUSH_METRIC_ORDER.map((key) => {
          const enabled = state.metrics[key];
          const followsTrainingDays = TRAINING_DAY_METRICS.includes(key);
          const title = t(
            `athlete-portal:clientConfiguration.trackers.${key}.title`
          );
          return (
            <div
              key={key}
              className={`push-dd-tile push-dd-metric${enabled ? "" : " is-off"}`}
            >
              <div className="push-dd-tile__text">
                <div className="push-dd-metric__title">
                  <p className="push-dd-tile__title">{title}</p>
                  {followsTrainingDays && (
                    <span className="push-dd-chip push-dd-chip--info">
                      {t("pushDailyDashboard.metric.trainingDays")}
                    </span>
                  )}
                  {isSyncableTracker(key) && (
                    <span className="push-dd-chip">
                      {t("athlete-portal:clientConfiguration.chips.syncable")}
                    </span>
                  )}
                </div>
                {enabled && !followsTrainingDays && (
                  <p className="push-dd-tile__sub">
                    {t("pushDailyDashboard.metric.everyday")}
                  </p>
                )}
              </div>
              <ConfigSwitch
                id={`push-dd-metric-${key}`}
                ariaLabel={t("pushDailyDashboard.metric.ariaLabel", {
                  metric: title,
                })}
                checked={enabled}
                onChange={(value: boolean) => onMetricChange(key, value)}
              />
            </div>
          );
        })}
      </div>
      {state.metrics.bodyMeasurements && (
        <div className="push-dd-body-parts">
          <div className="push-dd-body-parts__header">
            <p>
              {t("pushDailyDashboard.bodyParts.title")} ·{" "}
              {allSelected
                ? t("pushDailyDashboard.bodyParts.allSelected", {
                    count: state.bodyParts.length,
                  })
                : t("pushDailyDashboard.bodyParts.someSelected", {
                    count: state.bodyParts.length,
                  })}
            </p>
            <button
              type="button"
              className="push-dd-text-button"
              disabled={allSelected}
              onClick={() => onBodyPartsChange(allBodyParts)}
            >
              {t("pushDailyDashboard.bodyParts.selectAll")}
            </button>
          </div>
          <div className="push-dd-body-parts__chips">
            {allBodyParts.map((part) => {
              const selected = state.bodyParts.includes(part);
              const isLast = selected && onlyOneLeft;
              return (
                <button
                  key={part}
                  type="button"
                  className={`push-dd-body-part${selected ? " is-active" : ""}${isLast ? " is-last" : ""}`}
                  aria-pressed={selected}
                  aria-disabled={isLast || undefined}
                  title={isLast ? atLeastOne : undefined}
                  onClick={() =>
                    onBodyPartsChange(
                      toggleBodyPart(state.bodyParts, part, allBodyParts)
                    )
                  }
                >
                  {bodyPartLabel(part)}
                </button>
              );
            })}
          </div>
          {onlyOneLeft && (
            <output className="push-dd-body-parts__hint">{atLeastOne}</output>
          )}
        </div>
      )}
      <p className="push-dd-info" role="note">
        {t("pushDailyDashboard.info")}
      </p>
    </>
  );
};
